import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useMemo,
  useState
} from 'react';
import ContentLoader from 'react-content-loader';
import { useTranslation } from 'react-i18next';

import ThreeDots from './ThreeDots';
import Bio from './Bio';
import LabelStack from './LabelStack';
import VideoSlide from './VideoSlide';

import UIConfig from '@/UIConfig.ts';
import {
  CandidateProfilePhotosDto,
  CandidateUI
} from '@/api/matchmaker/types.ts';
import {
  AlcoholPreference,
  BodyType,
  EmploymentSector,
  SmokingStatus,
  ZodiacSign
} from '@/enums/apiEnums.ts';
import { ImageWithLoader } from '@/components/common/Image/ImageWithLoader.tsx';
import UserActivity from '@/components/Discovery/Card/Components/UserActivity.tsx';
import { Filter } from '@/components/Discovery/Card/Components/Filter.tsx';
import LocationShape from '@/components/common/svg/shapes/LocationShape';
import BriefcaseShape from '@/components/common/svg/shapes/BriefcaseShape';
import { helpers } from '@/helpers.ts';
import { getEmploymentSectors } from '@/components/Popup/EditProfile/EditEmploymentPopup.tsx';
import Modal from '@/animations/Modal.tsx';
import SearchFiltersModal from '@/components/Discovery/SearchFiltersModal.tsx';
import { useJoyrideTourStore } from '@/store/useJoyrideTourStore.ts';

type MediaItem =
  | { type: 'photo'; photo: CandidateProfilePhotosDto }
  | {
      type: 'video';
      videoUrl: string;
      posterUrl?: string;
      thumbhash?: string;
    };

export const ImageLoader = (props: { style?: CSSProperties }) => (
  <ContentLoader
    speed={1}
    width="100%"
    height="100%"
    backgroundColor="#303031"
    foregroundColor="#3f3f40"
    {...props}
  >
    <rect x="0" y="0" rx="0" ry="0" width="100%" height="100%" />
  </ContentLoader>
);

const AboutMeText = ({
  text,
  isExpanded,
  onToggle
}: {
  text: string;
  isExpanded: boolean;
  onToggle: () => void;
}) => (
  <div
    onClick={(e) => {
      e.stopPropagation();
      onToggle();
    }}
    style={{
      ...UIConfig.Typography.Card.AboutMe,
      ...(isExpanded
        ? {
            whiteSpace: 'pre-wrap',
            textAlign: 'left',
            marginTop: '8px',
            cursor: 'pointer'
          }
        : {
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'pre-wrap',
            textAlign: 'left',
            marginTop: '8px',
            cursor: 'pointer'
          })
    }}
  >
    {text.trim().replace(/\n{2,}/g, '\n')}
  </div>
);

const SubtitleItem = ({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) => (
  <div style={subtitleStyle}>
    {icon}
    <span
      style={{
        ...UIConfig.Typography.Card.SubTitle,
        marginLeft: '5px'
      }}
    >
      {text}
    </span>
  </div>
);

const OccupationSubtitle = ({
  occupation
}: {
  occupation: EmploymentSector;
}) => {
  const { t } = useTranslation();
  const employmentSectors = getEmploymentSectors(t);
  const label = employmentSectors[occupation];

  return (
    <SubtitleItem
      icon={<BriefcaseShape width={14} height={14} fillColor="#ffffff" />}
      text={label}
    />
  );
};

export const LocationSubtitle = ({
  geoProximity
}: {
  geoProximity: number;
}) => {
  const { t } = useTranslation();
  const distance =
    geoProximity > 0 && geoProximity <= 30
      ? helpers.getDistance(geoProximity, t)
      : t('ukraine');

  return (
    <SubtitleItem
      icon={
        <LocationShape
          width={15}
          height={15}
          fillColor="#ffffff"
          style={{ marginBottom: 1 }}
        />
      }
      text={distance}
    />
  );
};

const Content = ({
  matchCandidate,
  isPreview,
  toggleFirstPhoto,
  isDragging
}: {
  matchCandidate: CandidateUI;
  openSendGiftPopup?: () => void;
  isPreview?: boolean;
  toggleFirstPhoto?: boolean;
  isDragging?: boolean;
}) => {
  const {
    v2photos = [],
    v2videos = [],
    smokingStatus,
    alcoholPreference,
    bodyType,
    heightCm,
    zodiacSign
  } = matchCandidate.profile;

  const mediaItems: MediaItem[] = useMemo(() => {
    const items: MediaItem[] = [];

    if (v2photos.length > 0) {
      items.push({ type: 'photo', photo: v2photos[0] });
    }

    if (v2videos.length > 0) {
      const video = v2videos[0];
      const videoUrl = helpers.getVideoUrl(video.variants);
      if (videoUrl) {
        items.push({
          type: 'video',
          videoUrl,
          posterUrl: helpers.getVideoPoster(video.variants),
          thumbhash: helpers.getVideoThumbHash(video.variants)
        });
      }
    }

    for (let i = 1; i < v2photos.length; i++) {
      items.push({ type: 'photo', photo: v2photos[i] });
    }

    return items;
  }, [v2photos, v2videos]);

  const [isFirstPhoto, setIsFirstPhoto] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAboutMeExpanded, setIsAboutMeExpanded] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const { pauseTour, nextStep } = useJoyrideTourStore();

  const nextTourStep = () => {
    setTimeout(nextStep, 300);
  };

  const nextImage = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % mediaItems.length);
    setIsAboutMeExpanded(false);
  };

  const prevImage = () => {
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + mediaItems.length) % mediaItems.length
    );
    setIsAboutMeExpanded(false);
  };

  const toggleAboutMe = () => {
    setIsAboutMeExpanded((prev) => !prev);
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (mediaItems.length === 0) return;

    const { clientX, currentTarget } = event;
    const targetWidth = currentTarget?.offsetWidth;

    if (clientX < targetWidth / 2) {
      prevImage();
    } else {
      nextImage();
    }
  };

  const sections = useMemo(() => {
    const result: Array<{
      subtitle: ReactNode;
      content: JSX.Element | null;
    }> = [];

    const { employmentSector, geoProximity, aboutMe } = matchCandidate.profile;

    const hasOccupation =
      employmentSector !== EmploymentSector.EMPLOYMENT_SECTOR_UNSPECIFIED;
    const hasAboutMe = !!aboutMe;

    const allLabels = [
      'heightCm',
      'zodiacSign',
      'bodyType',
      'smokingStatus',
      'alcoholPreference'
    ];
    const availableLabels = allLabels.filter((label) => {
      if (label === 'heightCm') return heightCm > 0;
      if (label === 'zodiacSign')
        return zodiacSign !== ZodiacSign.ZODIAC_SIGN_UNSPECIFIED;
      if (label === 'bodyType')
        return bodyType !== BodyType.BODY_TYPE_UNSPECIFIED;
      if (label === 'smokingStatus')
        return smokingStatus !== SmokingStatus.SMOKING_STATUS_UNSPECIFIED;
      if (label === 'alcoholPreference')
        return (
          alcoholPreference !== AlcoholPreference.ALCOHOL_PREFERENCE_UNSPECIFIED
        );
      return false;
    });

    const totalLabels = availableLabels.length;

    // Case 1: Only 1 media item
    if (mediaItems.length === 1) {
      result.push({
        subtitle: hasOccupation ? (
          <OccupationSubtitle occupation={employmentSector} />
        ) : (
          <LocationSubtitle geoProximity={geoProximity} />
        ),
        content: hasAboutMe ? (
          <AboutMeText
            text={aboutMe}
            isExpanded={isAboutMeExpanded}
            onToggle={toggleAboutMe}
          />
        ) : totalLabels > 0 ? (
          <LabelStack
            matchCandidate={matchCandidate}
            showOnly={availableLabels.slice(0, 3)}
            maxLabels={3}
          />
        ) : null
      });
    }
    // Case 2: 2+ photos
    else {
      // Photo 0
      result.push({
        subtitle: hasOccupation ? (
          <OccupationSubtitle occupation={employmentSector} />
        ) : null,
        content: hasAboutMe ? (
          <AboutMeText
            text={aboutMe}
            isExpanded={isAboutMeExpanded}
            onToggle={toggleAboutMe}
          />
        ) : null
      });

      // Photo 1
      result.push({
        subtitle: <LocationSubtitle geoProximity={geoProximity} />,
        content:
          totalLabels > 0 ? (
            <LabelStack
              matchCandidate={matchCandidate}
              showOnly={availableLabels.slice(0, 3)}
              maxLabels={3}
              showExpandButton={totalLabels > 3}
              totalLabels={totalLabels}
            />
          ) : null
      });

      // Photo 2+
      if (mediaItems.length >= 3 && totalLabels > 3) {
        result.push({
          subtitle: <LocationSubtitle geoProximity={geoProximity} />,
          content: (
            <LabelStack
              matchCandidate={matchCandidate}
              showOnly={availableLabels.slice(3)}
            />
          )
        });
      }

      // Fill remaining sections with distance
      while (result.length < mediaItems.length) {
        result.push({
          subtitle: <LocationSubtitle geoProximity={geoProximity} />,
          content: null
        });
      }
    }

    return result;
  }, [matchCandidate, mediaItems.length, isAboutMeExpanded]);

  useEffect(() => {
    if (isPreview) {
      setCurrentIndex(0);
    }
  }, [toggleFirstPhoto]);

  useEffect(() => {
    if (currentIndex === 0) {
      setIsFirstPhoto(true);
    } else {
      setIsFirstPhoto(false);
    }
  }, [currentIndex]);

  return (
    <>
      <div
        style={{
          ...sliderStyles,
          ...(isPreview ? { height: '100%' } : {})
        }}
      >
        <div
          style={{
            ...imageContainerStyle
          }}
          onClick={(e) => handleClick(e)}
        >
          <ImageLoader
            style={{
              borderBottomRightRadius: UIConfig.Card.Content.borderRadius,
              borderBottomLeftRadius: 0,
              position: 'absolute',
              height: '100%',
              width: '100%',
              zIndex: 0
            }}
          />
          {mediaItems.map((item, i) => {
            const shouldRender =
              i === currentIndex ||
              i === currentIndex - 1 ||
              i === currentIndex + 1;
            if (!shouldRender) return null;

            return (
              <div
                key={i}
                style={{
                  display: i === currentIndex ? 'block' : 'none',
                  position: 'absolute',
                  height: '100%',
                  width: '100%',
                  zIndex: 0
                }}
              >
                {item.type === 'photo' ? (
                  <ImageWithLoader
                    src={helpers.getPrimaryPhoto(item.photo?.variants) ?? ''}
                    thumbhash={helpers.getThumbHash(item.photo?.variants)}
                    alt="carousel image"
                    style={{
                      ...imageStyle,
                      height: '100%'
                    }}
                  />
                ) : (
                  <VideoSlide
                    videoUrl={item.videoUrl}
                    posterUrl={item.posterUrl}
                    thumbhash={item.thumbhash}
                    candidateName={matchCandidate.profile.name}
                    isActive={i === currentIndex}
                    isDragging={isDragging}
                    isPreview={isPreview}
                  />
                )}
              </div>
            );
          })}
          {mediaItems.length >= 1 && (
            <ThreeDots
              activeIndex={currentIndex}
              imagesCount={mediaItems.length}
              mediaTypes={mediaItems.map((item) => item.type)}
            />
          )}
          {isFirstPhoto &&
            matchCandidate.activityLabels &&
            mediaItems[currentIndex]?.type !== 'video' && (
              <UserActivity
                labels={matchCandidate.activityLabels}
                gender={matchCandidate.profile.gender}
                isPreview={isPreview}
              />
            )}
          {!isPreview && (
            <Filter
              onClick={() => {
                pauseTour();
                setShowFilters(true);
              }}
            />
          )}
          <Bio
            matchCandidate={matchCandidate}
            subtitle={
              mediaItems[currentIndex]?.type === 'video' ? (
                <LocationSubtitle
                  geoProximity={matchCandidate.profile.geoProximity}
                />
              ) : (
                sections[currentIndex]?.subtitle
              )
            }
            content={
              mediaItems[currentIndex]?.type === 'video'
                ? null
                : sections[currentIndex]?.content
            }
            isPreview={isPreview}
          />
        </div>
      </div>

      <Modal
        open={showFilters}
        handleClose={() => {
          nextTourStep();
          setShowFilters(false);
        }}
      >
        <SearchFiltersModal
          onClose={() => {
            nextTourStep();
            setShowFilters(false);
          }}
        />
      </Modal>
    </>
  );
};

export const sliderStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  position: 'relative',
  width: '100%',
  height: 'calc(100% - 70px)',
  overflow: 'hidden',
  cursor: 'pointer',
  borderRadius: UIConfig.Card.Content.borderRadius,
  borderBottomLeftRadius: 0
};

export const imageContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
  position: 'relative',
  background: UIConfig.Card.Content.background
};

export const imageStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderBottomRightRadius: UIConfig.Card.Content.borderRadius,
  borderBottomLeftRadius: 0
};

const subtitleStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  marginBottom: '10px'
};

export default Content;
